import Bluebird from 'bluebird'
import { moduleState, state } from 'cerebral'
import { assocPath, isEmpty, mergeDeepRight, path, pick, toLower } from 'ramda'

import { DateTime } from 'luxon'
import { isNilOrEmpty, isNumber, omitBy } from 'ramda-adjunct'
import mapbox from '../../../shared/mapbox'
import schemaSettings from '../../../shared/schemas/mobile-user-invitation-schema'
import { plural } from '../../lib/util/format'
import { crudActions } from '../factories'

const { invitationImportSchema } = schemaSettings

const { getGeoCoordinates } = mapbox
const actions = crudActions({ name: 'invitations' })

export const { find, setList, clearList, get, remove, save: update } = actions

const omitEmpty = omitBy(isNilOrEmpty)

const parseDate = (date) => {
  if (date.isValid) {
    return date
  }
  const parsed = DateTime.fromFormat(date, 'MM/dd/yyyy', { zone: 'utc' })
  if (parsed.isValid) {
    return parsed
  }
  return DateTime.invalid('Unparsable date')
}

export const save = async (context) => {
  const { store, props: { values: { userType, address, ...values } } = {}, app } = context
  const { sendCommandCenterInvite = false } = values
  let { accountSetting: accountSettingId } = values

  // TODO: Getting sequences and running them from inside an action should be forbidden!
  // This should be refactored to use sequences properly, but that is too ambitious for the current bug fixing.
  const saveProfile = app.getSequence('profiles.saveWithoutFeedback')
  const saveAccountSetting = app.getSequence('accountSettings.saveWithoutFeedback')

  if (address && !isEmpty(omitEmpty(address))) {
    try {
      const { street1, street2, city, state, zip } = address
      const addressCoordinates = await getGeoCoordinates({ street1, street2, city, state, zip })
      if (isNumber(addressCoordinates?.latitude) && isNumber(addressCoordinates?.longitude)) {
        values.address = address
        values.addressCoordinates = addressCoordinates
      }
    } catch (error) {
      console.error({ err: error }, 'Could not update profile address geo coordinates.')
    }
  }

  if (userType) {
    values.userTypes = [userType]
  }

  let result = await saveProfile({ values: { ...values, _id: values.profile } })
  const { entities: { profiles: [profile = {}] } = {} } = result || {}

  if (sendCommandCenterInvite) {
    try {
      const accountSettingValues = pick(['email', 'name', 'packages'], values)
      const accountSettingResult = await saveAccountSetting({
        values: { ...accountSettingValues, skipTokenGeneration: false, mobileUserProfile: profile._id, _id: accountSettingId },
      })
      // Accessing this is so magical because we are using sequences wrong.
      accountSettingId = path(['entities', 'accountSettings', 0, '_id'], accountSettingResult)
    } catch (error) {
      const message =
        "We encountered an unexpected error to create the command center invitation. Please try again, or contact us to let us know you're having trouble."
      store.set(moduleState`invitations.error`, message)
      throw error
    }
  }

  const invitation = { ...values, profile: profile._id, accountSetting: accountSettingId }
  // Setting this is so magical because we are using sequences wrong.
  const invitationResult = await actions.save(assocPath(['props', 'values'], invitation, context))
  result = mergeDeepRight(result, invitationResult)

  return result
}

export const changeRole = async ({ props: { id, userType }, invitationsService }) => {
  await invitationsService.patch(id, { userType })
}

export const setBulkQueue = async ({ props, store, get }) => {
  const { queue } = props
  const isRideProductsEnabled = get(state`account.packages.ride-products`)

  const invites = queue.map(
    ({
      first_name,
      last_name,
      phone,
      role = '',
      email,
      level_of_care = '',
      send_invite = true,
      associated_with,
      track_attendance,
      birthdate,
      move_in_date,
      street_1,
      street_2,
      city,
      state,
      zip,
      ride_product,
    }) => {
      const data = {
        name: { first: first_name, last: last_name },
        userType: toLower(role),
        associatedWith: associated_with,
        trackAttendance: track_attendance,
        send: send_invite,
        email,
        levelOfCare: level_of_care,
        address: { street1: street_1, street2: street_2, city, state, zip },
        phone,
      }

      if (birthdate) {
        const parsedBirthdate = parseDate(birthdate)
        if (parsedBirthdate.isValid) {
          data.birthdate = parsedBirthdate
        }
      }

      if (move_in_date) {
        const parsedMoveInDate = parseDate(move_in_date)
        if (parsedMoveInDate.isValid) {
          data.moveInDate = parsedMoveInDate
        }
      }

      if (isRideProductsEnabled && ride_product) {
        data.product = ride_product
      }

      const invite = invitationImportSchema.validateSync(data)
      return { invite, status: 'ready' }
    }
  )
  store.set(moduleState`upload.queue`, invites)
}

export const resolveDuplicates = async ({ props: { invites = [] }, store }) => {
  store.set(
    moduleState`upload.queue`,
    invites.map((invite) => ({ invite, status: 'ready' }))
  )
}

export const processBulkQueue = async ({ store, get, app }) => {
  const showFeedback = app.getSequence('feedback.showFeedback')
  const saveInvitation = app.getSequence('invitations.saveBulk')
  const patchProfile = app.getSequence('profiles.patch')
  const queue = get(moduleState`upload.queue`)
  store.set(moduleState`upload.loading`, true)
  let sent = 0
  let updated = 0
  let created = 0
  await Bluebird.map(
    queue,
    async ({ invite }, index) => {
      try {
        // If there's an id in the bulk queue, it's not actually a new invite.
        if (invite?._id) {
          const { _id: id, ...values } = invite
          await patchProfile({ id, values })
          updated++
        } else {
          // TODO: Only send invitations to users without existing invitations.
          await saveInvitation({ values: invite })
          created++
          if (invite.send) {
            sent++
          }
          store.increment(moduleState`upload.created`)
        }
        store.set(moduleState`upload.queue.${index}.status`, 'success')
      } catch (error) {
        store.merge(moduleState`upload.queue.${index}`, { status: 'error', error: error.message })
      }
      await Bluebird.delay(500)
    },
    { concurrency: 5 }
  )
  store.set(moduleState`upload.success`, true)
  store.set(moduleState`upload.loading`, false)

  showFeedback({
    title: [
      created && `${plural(created, 'profile', { showCount: true })} have been created.`,
      updated && `${plural(updated, 'profile', { showCount: true })} have been updated.`,
      `${plural(sent, 'invitation', { showCount: true })} have been sent.`,
    ]
      .filter(Boolean)
      .join(' '),
    type: 'success',
  })
}

export const openUploadForm = ({ store }) => {
  store.set(moduleState`upload.visible`, true)
}

export const closeUploadForm = ({ store }) => {
  store.set(moduleState`upload.visible`, false)
}
